import React, { useState, useEffect } from "react";
import {
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Calendar,
} from "lucide-react";
import axios from "axios";
import { useTheme } from "../contexts/ThemeContext";

export default function ZohoSyncLogs({ companyId }) {
  const { isDarkMode } = useTheme();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    loadLogs();
  }, [companyId, page]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/zoho/${companyId}/sync-logs`, {
        params: { page, limit: 20 },
      });

      setLogs(response.data.logs);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error("Failed to load sync logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (startedAt, completedAt) => {
    if (!completedAt) return "In progress";

    const duration = new Date(completedAt) - new Date(startedAt);
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case "error":
        return <XCircle className="w-5 h-5 text-red-500" />;
      case "in_progress":
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: isDarkMode
        ? "bg-green-900/30 text-green-400"
        : "bg-green-100 text-green-700",
      error: isDarkMode
        ? "bg-red-900/30 text-red-400"
        : "bg-red-100 text-red-700",
      in_progress: isDarkMode
        ? "bg-blue-900/30 text-blue-400"
        : "bg-blue-100 text-blue-700",
    };

    return (
      badges[status] ||
      (isDarkMode ? "bg-gray-800 text-gray-400" : "bg-gray-100 text-gray-700")
    );
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw
          className={`w-6 h-6 animate-spin ${
            isDarkMode ? "text-blue-400" : "text-blue-600"
          }`}
        />
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg shadow-sm border ${
        isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <div
        className={`p-6 border-b ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <h3
          className={`text-lg font-semibold ${
            isDarkMode ? "text-white" : "text-gray-900"
          }`}
        >
          Sync History
        </h3>
      </div>

      <div
        className={`divide-y ${isDarkMode ? "divide-gray-700" : "divide-gray-200"}`}
      >
        {logs.length === 0 ? (
          <div
            className={`p-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            No sync history available
          </div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className="p-4">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() =>
                  setExpandedLog(expandedLog === log.id ? null : log.id)
                }
              >
                <div className="flex items-center gap-3 flex-1">
                  {getStatusIcon(log.status)}

                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`font-medium ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {log.syncType === "full" ? "Full Sync" : "Partial Sync"}
                      </span>
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(
                          log.status,
                        )}`}
                      >
                        {log.status.replace("_", " ")}
                      </span>
                    </div>

                    <div
                      className={`flex items-center gap-4 text-sm ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(log.startedAt).toLocaleString()}
                      </span>
                      <span>
                        Duration:{" "}
                        {formatDuration(log.startedAt, log.completedAt)}
                      </span>
                      {log.user && (
                        <span>
                          By {log.user.firstName} {log.user.lastName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {expandedLog === log.id ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>

              {expandedLog === log.id && (
                <div
                  className={`mt-4 pt-4 border-t ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  {log.error && (
                    <div
                      className={`mb-4 p-3 rounded-lg ${
                        isDarkMode
                          ? "bg-red-900/20 border border-red-900/50"
                          : "bg-red-50 border border-red-200"
                      }`}
                    >
                      <p
                        className={`text-sm ${
                          isDarkMode ? "text-red-300" : "text-red-700"
                        }`}
                      >
                        {log.error}
                      </p>
                    </div>
                  )}

                  {log.stats && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(log.stats).map(([key, value]) => (
                        <div
                          key={key}
                          className={`text-center p-3 rounded-lg ${
                            isDarkMode ? "bg-gray-800/50" : "bg-gray-50"
                          }`}
                        >
                          <div
                            className={`text-2xl font-bold ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {value}
                          </div>
                          <div
                            className={`text-sm capitalize ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {key.replace(/([A-Z])/g, " $1").trim()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {log.details && (
                    <div className="mt-4">
                      <h4
                        className={`font-medium mb-2 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Details
                      </h4>
                      <pre
                        className={`p-3 rounded-lg text-xs overflow-x-auto ${
                          isDarkMode
                            ? "bg-gray-800/50 text-gray-300"
                            : "bg-gray-50 text-gray-700"
                        }`}
                      >
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div
          className={`p-4 border-t flex items-center justify-between ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode
                ? "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            Previous
          </button>

          <span
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Page {page} of {totalPages}
          </span>

          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode
                ? "bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700"
                : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            }`}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
